import { type Maybe } from '@adornis/base/utilTypes.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter } from 'rxjs';

@customElement('bh-prompt-category-modify')
export class BHPromptCategoryModify extends TrDialog<Maybe<Category>> {
  @property() mode = new RXController<Maybe<'add' | 'edit'>>(this, undefined);
  @property({ attribute: false }) category = new RXController<Maybe<Category>>(this, undefined);

  @state() private readonly _form = new FormController(this, this.category.observable.pipe(filter(Boolean)), {
    success: async doc => {
      if (this.mode.value === 'add') {
        await doc.create();
      }
      if (this.mode.value === 'edit') {
        await doc.save();
      }
      return this.close(doc);
    },
    error: err => {
      console.log(err);
    },
  });

  override render() {
    if (!this.mode.value || this.category.isLoading || !this.category.value || !this._form.document) {
      return html` <x-loading-state no-wrapper></x-loading-state> `;
    }

    return html`
      <x-flex space="md">
        ${this.mode.value === 'add'
          ? html`<x-h2>${this._translation.translate('categories', 'add')}</x-h2>`
          : html`<x-h2>${this._translation.translate('categories', 'edit')}</x-h2>`}

        <tr-input ${this._form.field('name')} placeholder="Name"></tr-input>

        <tr-input
          ${this._form.field('sortingWeight')}
          type="number"
          placeholder=${this._translation.translate('field', 'sortingWeight')}
        ></tr-input>

        <x-flex horizontal space-between crossaxis-center space="md" wrap>
          <x-button mode="outline" @click=${() => this.cancel()}>${this._translation.translate('discard')}</x-button>
          <x-button @click=${() => this._form.validate()}>${this._translation.translate('save')}</x-button>
        </x-flex>
      </x-flex>
    `;
  }

  static add(institutionID: string): Promise<Maybe<Category>> {
    return this.showPopup({
      props: { mode: 'add', category: new Category({ institutionID }) },
    });
  }

  static edit(category: Category): Promise<Maybe<Category>> {
    return this.showPopup({ props: { mode: 'edit', category } });
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close(undefined);
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  static override get element_name() {
    return 'bh-prompt-category-modify';
  }
}
